import { localizeSegment } from "@/app/(site)/_utils/translateCanonicalSegments"
import { SHIPPING_COUNTRIES } from "@/data/shipping-countries"
import { SHIPPING_COUNTRY_CODES } from "@/data/shipping-country-code"
import { euLocales } from "@/i18n/locales"
import { type ProductMetaData } from "@/lib/content-service/types/ContentServiceProduct"
import logger from "@/lib/logger/logger-init"
import { UNIVERSAL_DEFAULT_LANGUAGE } from "@/lib/siteconfig/constants"
import { type HreflangKeys, type HreflangMap } from "@/src/utils/hreflangs/types"

import { isLiveAppLocale } from "@/i18n/apps"

/**
 * Get country code from locale string
 * @param locale e.g. "en", "en-gb", "de-ch"
 * @returns ISO 3166-1 alpha-2 country code or "Global" for "en"
 */
const getCountryCodeFromLocale = (locale: string): string => {
    if (!locale || locale.length < 2) {
        logger.info(`No locale provided: ${locale}. Returning Global EN`)
        return "Global EN"
    }

    // For locale with country code (e.g. "en-gb")
    if (locale.includes("-")) {
        const countryCode = locale.split("-")[1].toUpperCase()
        // Special case for UK
        if (countryCode === "GB") {
            return "UK"
        }
        return countryCode
    }

    // For 2-letter country codes
    return locale.toUpperCase()
}

/**
 * Get country code from country name
 * @param countryName e.g. "United Kingdom", "Switzerland"
 * @returns ISO 3166-1 alpha-2 country code (with UK instead of GB)
 */
export const getCountryCodeFromLocaleOrCountryName = (
    locale: string,
    countryName: string
): string => {
    if (locale === "en") {
        return "Global EN"
    }
    if (locale === "de") {
        return "Global DE"
    }

    if (!countryName) {
        return getCountryCodeFromLocale(locale)
    }

    // Find the country key in SHIPPING_COUNTRIES
    const countryKey = Object.entries(SHIPPING_COUNTRIES).find(
        ([, value]) => value.toLowerCase() === countryName.toLowerCase()
    )?.[0]

    if (!countryKey) {
        return getCountryCodeFromLocale(locale)
    }

    // Get the code from SHIPPING_COUNTRY_CODES
    const countryCode =
        SHIPPING_COUNTRY_CODES[countryKey as keyof typeof SHIPPING_COUNTRY_CODES]

    // Special case for UK
    if (countryCode === "gb") {
        return "UK"
    }

    return countryCode.toUpperCase()
}

/**
 * Generates product-specific hreflang URLs using product hrefs data.
 *
 * @param {ProductMetaData["hrefs"]} productHrefs - Object containing locale keys and their corresponding product URIs
 * @returns {HreflangMap} Object with hreflang keys and their corresponding URLs
 *
 * This function creates hreflang URLs for product pages using the new
 * API new endpoint. It only includes x-default if all euLocales are present in the hrefs.
 *
 * Example usage:
 * const hrefs = {
 *   'en-us': 'otocon-lead-blue-matt',
 *   'en-au': 'otocon-as-nzs-lead-blue-matt',
 *   de: 'otocon-lead-blue-matt',
 *   // ... more locales
 * }
 * const hreflangs = generateProductHrefLangs(hrefs);
 */
export const generateProductHrefLangs = (
    productHrefs: ProductMetaData["hrefs"]
): HreflangMap => {
    const siteUrl = process.env.NEXT_PUBLIC_SITEMAP_DOMAIN || "https://localhost:3000"

    // Check if all euLocales are present in the productHrefs
    const allEuLocalesPresent = euLocales.every((locale: string) =>
        productHrefs.hasOwnProperty(locale)
    )

    const hreflangs: Partial<HreflangMap> = {}

    // Generate URLs for each locale that has a product href
    Object.entries(productHrefs).forEach(([locale, productUri]) => {
        if (!isLiveAppLocale(locale)) return

        const hreflangKey = locale as HreflangKeys
        const localizedProductSegement = localizeSegment(locale, "product")
        hreflangs[hreflangKey] =
            `${siteUrl}/${locale}/${localizedProductSegement}/${productUri}`
    })

    // Only add x-default if all euLocales are present
    if (allEuLocalesPresent) {
        hreflangs["x-default"] =
            `${siteUrl}/${UNIVERSAL_DEFAULT_LANGUAGE}/product/${productHrefs[UNIVERSAL_DEFAULT_LANGUAGE] || productHrefs.en || ""}`
    }

    return hreflangs as HreflangMap
}

/**
 * Generates an article href from translated slug, fallback slug, and preSlug.
 *
 * @param article - Article object with optional translatedSlug, preSlug, and slug
 * @param language - Language code to use for translated slug lookup
 * @returns The href path, or empty string if no slug is available
 *
 * Priority:
 * 1. translatedSlug[language]?.current (if available)
 * 2. slug (fallback)
 * 3. "" (if neither is available)
 */
export function getArticleHref(
    article: {
        translatedSlug?: Partial<Record<string, string>>
        preSlug?: string
        slug?: string
    },
    language: string
): string {
    const translatedSlug = article?.translatedSlug?.[language]
    if (translatedSlug && article?.preSlug) {
        return `/${article.preSlug}${translatedSlug}`
    }

    if (article?.slug && article?.preSlug) {
        return `/${article.preSlug}${article.slug}`
    }

    return ""
}
